import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, IconButton, Modal, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { ArrowBackButtonIcon, ModalCloseButtonIcon, WhyCreateGroupButtonIcon } from "../assets/icons/groups-icons";
import { getEntityProfileRoute } from "../../../routers/routeUtils";

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  descriptionBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px 11px 12px",
    borderRadius: "8px",
    background: "var(--Green---25, #C2E6C4)",
  },
  description: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  whyCreateButton: {
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    width: "fit-content",
    background: "transparent",
  },
  modal: {
    width: { xs: "90%", md: "485px" },
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "16px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  modalHeader: {
    position: "relative",
    paddingTop: "21px",
  },
  modalTitle: {
    textAlign: "center",
    color: "var(--Black, #151617)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  closeButton: {
    position: "absolute",
    top: "16px",
    right: "22px",
  },
  modalContent: {
    px: "24px",
  },
  modalSubtitle: {
    color: "var(--Black, #151617)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    mb: "24px",
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  listItem: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
    listStyle: "disc",
    listStylePosition: "inside",
  },
  gotItButton: {
    mx: "auto",
    maxWidth: "calc(100% - 48px)",
    mb: "16px",
  },
  headingContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

export const Heading = ({ title }: { title: string }) => {
  const navigate = useNavigate();
  const { entityId } = useParams<{ entityId: string }>();

  const { t } = useTranslation("groups");
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  return (
    <Box sx={styles.container}>
      <Box sx={styles.headingContainer}>
        <IconButton onClick={() => navigate(getEntityProfileRoute(Number(entityId)))}>
          <ArrowBackButtonIcon />
        </IconButton>
        <Typography sx={styles.title}>{title}</Typography>
      </Box>
      <Box sx={styles.descriptionBox}>
        <Typography sx={styles.description}>{t("createGroupDescription")}</Typography>
        <Button sx={styles.whyCreateButton} endIcon={<WhyCreateGroupButtonIcon />} onClick={handleOpen}>
          {t("whyCreateGroup")}
        </Button>
      </Box>
      <WhyCreateGroupModal open={open} closeModal={handleClose} />
    </Box>
  );
};

const WhyCreateGroupModal = ({ open, closeModal }: { open: boolean; closeModal: () => void }) => {
  const { t } = useTranslation("groups");

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={styles.modal}>
        <Box sx={styles.modalHeader}>
          <Typography sx={styles.modalTitle}>{t("whyCreateGroup")}</Typography>
          <IconButton onClick={closeModal} sx={styles.closeButton} size="small">
            <ModalCloseButtonIcon />
          </IconButton>
        </Box>
        <Divider variant="fullWidth" orientation="horizontal" />
        <Box sx={styles.modalContent}>
          <Typography sx={styles.modalSubtitle}>{t("makeDecisionEasy")}</Typography>
          <Box component="ul" sx={styles.listContainer}>
            <Typography component="li" sx={styles.listItem}>
              {t("inviteGroupMembers")}
            </Typography>
            <Typography component="li" sx={styles.listItem}>
              {t("createVerifiedGroups")}
            </Typography>
          </Box>
        </Box>
        <Divider variant="fullWidth" orientation="horizontal" />
        <Button onClick={closeModal} variant="primary-sm" fullWidth sx={styles.gotItButton}>
          {t("gotIt")}
        </Button>
      </Box>
    </Modal>
  );
};
